import axios from 'axios'
import { useApp } from 'lodestar-app-element/src/contexts/AppContext'
import { useAuth } from 'lodestar-app-element/src/contexts/AuthContext'
import React, { createContext, useEffect, useState } from 'react'
import { getFileDownloadableLink } from '../helpers'

export type AudioPlayerMode = 'list-loop' | 'single-loop' | 'random' | 'sequential'

type AudioPlayerContextValue = {
  title: string
  contentSectionTitle: string
  audioUrl: string
  isPlaying: boolean
  isBackgroundMode: boolean
  programId: string
  videoId: string
  mimeType: string
  contentType: string | null
  source: string
  contentId: string
  visible: boolean
  changePlayingState?: (state: boolean) => void
  changeGlobalPlayingState?: (state: boolean) => void
  changeBackgroundMode?: (state: boolean) => void
  close?: () => void
  setup?: (options: {
    title?: string
    contentSectionTitle?: string
    programId?: string
    contentId: string
    backgroundMode?: boolean
    contentType?: string | null
    videoId?: string
    source?: string
    cloudfront?: object
  }) => void
  setProgramId?: (programId: string) => void
}

const defaultAudioPlayerContext: AudioPlayerContextValue = {
  title: '',
  contentSectionTitle: '',
  audioUrl: '',
  programId: '',
  mimeType: '',
  source: '',
  contentId: '',
  videoId: '',
  visible: false,
  contentType: null,
  isPlaying: true,
  isBackgroundMode: false,
}

const AudioPlayerContext = createContext<AudioPlayerContextValue>(defaultAudioPlayerContext)

export const AudioPlayerProvider: React.FC = ({ children }) => {
  const { id: appId } = useApp()
  const { authToken } = useAuth()
  const localPlaying = localStorage.getItem('playing')
  const playing: {
    visible: boolean
    programId: string
    contentId: string
    videoId: string
    backgroundMode: boolean
    contentType: string
    source: string
    cloudfront?: object
  } = localPlaying !== null && JSON.parse(localPlaying)
  const [visible, setVisible] = useState(playing.visible || false)
  const [programId, setProgramId] = useState(playing.programId || '')
  const [contentId, setContentId] = useState(playing.contentId || '')
  const [isBackgroundMode, setIsBackgroundMode] = useState(playing.backgroundMode || false)
  const [contentType, setContentType] = useState(playing.contentType || null)
  const [source, setSource] = useState(playing.source || '')
  const [cloudfrontPath, setCloudfrontPath] = useState(playing.cloudfront)
  const [videoId, setVideoId] = useState(playing.videoId || '')
  const [isPlaying, setIsPlaying] = useState(false)
  const [title, setTitle] = useState('')
  const [contentSectionTitle, setContentSectionTitle] = useState('')
  const [audioUrl, setAudioUrl] = useState('')
  const [mimeType, setMimeType] = useState('')

  useEffect(() => {
    if (contentType === 'audio') {
      getFileDownloadableLink(`audios/${appId}/${programId}/${contentId}`, authToken).then(audioUrl => {
        setAudioUrl(audioUrl)
        setMimeType('')
      })
    }
    if (contentType === 'video') {
      if (source === 'azure') {
        setMimeType('application/x-mpegURL')
        setAudioUrl(`${source}(format=m3u8-cmaf)`)
      }

      if (cloudfrontPath) {
        axios
          .get(`${process.env.REACT_APP_LODESTAR_SERVER_ENDPOINT}/videos/${videoId}/sign`, {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          })
          .then(({ data }) => {
            const {
              videoSignedPaths: { hlsPath, dashPath, cloudfrontMigratedHlsPath },
            } = data.result

            setMimeType('application/x-mpegURL')
            setAudioUrl(
              hlsPath && dashPath
                ? `${process.env.REACT_APP_LODESTAR_SERVER_ENDPOINT}/videos${hlsPath}`
                : `${process.env.REACT_APP_LODESTAR_SERVER_ENDPOINT}/videos${cloudfrontMigratedHlsPath}`,
            )
          })

        return
      }

      if (source === 'cloudflare') {
        axios
          .post(
            `${process.env.REACT_APP_API_BASE_ROOT}/videos/${videoId}/token`,
            {},
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            },
          )
          .then(({ data }) => {
            if (data.code === 'SUCCESS') {
              setMimeType('application/x-mpegURL')
              setAudioUrl(`https://cloudflarestream.com/${data.result.token}/manifest/video.m3u8`)
            }
          })
      }
    }
  }, [appId, authToken, contentId, contentType, programId, source, videoId])

  return (
    <AudioPlayerContext.Provider
      value={{
        visible,
        title,
        contentSectionTitle,
        audioUrl,
        mimeType,
        contentType,
        isPlaying,
        videoId,
        source,
        isBackgroundMode,
        programId,
        contentId,
        changePlayingState: state => {
          setIsPlaying(state)
        },
        changeBackgroundMode: state => {
          setIsBackgroundMode(state)
        },
        changeGlobalPlayingState: state => {
          setVisible(state)
        },
        close: () => {
          setVisible(false)
          const playing = {
            backgroundMode: isBackgroundMode,
            isPlaying: false,
            visible: false,
            programId: '',
            contentId: '',
          }
          localStorage.setItem('playing', JSON.stringify(playing))
          localStorage.setItem('audioPlayerVisibleState', 'close')
        },
        setup: options => {
          const {
            title,
            contentSectionTitle,
            programId,
            contentId,
            backgroundMode,
            contentType,
            videoId,
            source,
            cloudfront,
          } = options
          title && setTitle(title)
          contentSectionTitle && setContentSectionTitle(contentSectionTitle)
          programId && setProgramId(programId)
          contentId && setContentId(contentId)
          contentType && setContentType(contentType)
          videoId && setVideoId(videoId)
          source && setSource(source)
          cloudfront && setCloudfrontPath(cloudfront)
          const playing = {
            backgroundMode,
            visible,
            programId,
            contentId,
            videoId,
            source,
            contentType,
            cloudfront,
          }
          localStorage.setItem('playing', JSON.stringify(playing))
          localStorage.setItem('audioPlayerVisibleState', 'open')
        },
        setProgramId: (programId: string) => {
          setProgramId(programId)
        },
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  )
}

export default AudioPlayerContext
